<template>
    <div class="box">
        <h2 class="title is-4">New .299</h2>
        
        <!-- Aircraft Tail Number Section -->
        <div class="columns is-multiline">
            <div class="column is-full">
                <SearchDropdown
                    label="Aircraft Tail Number"
                    placeholder="Search for tail number"
                    :loading="tailNumbersLoading"
                    loadingText="Loading tail numbers..."
                    :items="aircrafts"
                    v-model="selectedAircraft"
                    v-model:searchText="tailNumberSearch"
                    :validationError="aircraftValidationError"
                    :touched="aircraftTouched"
                    noResultsText="No matching tail numbers found"
                    :searchFields="['value']"
                    displayField="value"
                    @select="selectAircraft"
                    @blur="handleAircraftBlur"
                />
            </div>
        
            <!-- Legs Section (Only shown if aircraft is valid) -->
            <div class="column is-full" v-if="isAircraftValid">
                <label class="label">Available Legs</label>
                <div class="field">
                    <div v-if="legsLoading" class="has-text-centered p-4">
                        <span class="icon is-large">
                            <font-awesome-icon :icon="['fas', 'spinner']" spin />
                        </span>
                        <p>Loading available legs...</p>
                    </div>
                    <div v-else-if="availableLegs.length === 0" class="has-text-centered p-4">
                        <span class="icon is-large">
                            <font-awesome-icon :icon="['fas', 'info-circle']" />
                        </span>
                        <p>No legs available for the selected aircraft.</p>
                    </div>
                    <div v-else class="table-container">
                        <table class="table is-fullwidth is-striped is-hoverable">
                            <thead>
                                <tr>
                                    <th class="has-text-centered">Select</th>
                                    <th class="has-text-centered">Date & Time</th>
                                    <th class="has-text-centered">Departure</th>
                                    <th class="has-text-centered">Arrival</th>
                                    <th class="has-text-centered">PIC</th>
                                    <th class="has-text-centered">SIC</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="leg in availableLegs" 
                                    :key="leg.id"
                                    @click="toggleLegSelection(leg)"
                                    class="cursor-pointer">
                                    <td class="has-text-centered">
                                        <label class="checkbox">
                                            <input type="checkbox" 
                                                   :value="leg"
                                                   v-model="selectedLegs"
                                                   @click.stop>
                                        </label>
                                    </td>
                                    <td class="has-text-centered">
                                        {{ formatDate(leg.departureTime) }} {{ formatTime(leg.departureTime) }}
                                    </td>
                                    <td class="has-text-centered">
                                        {{ leg.departureAirport }}
                                    </td>
                                    <td class="has-text-centered">
                                        {{ leg.arrivalAirport }}
                                    </td>
                                    <td class="has-text-centered">
                                        {{ getPilotName(leg, 2) || 'Not assigned' }}
                                    </td>
                                    <td class="has-text-centered">
                                        {{ getPilotName(leg, 3) || 'Not assigned' }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
                <div v-if="legsValidationError && legsTouched" class="notification is-danger is-light">
                    {{ legsValidationError }}
                </div>
            </div>

            <!-- Check Airman Section (Only shown if legs are valid) -->
            <div class="column is-full" v-if="isAircraftValid && isLegsValid">
                <SearchDropdown
                    label="Check Airman"
                    placeholder="Search for check airman"
                    :loading="checkAirmansLoading"
                    loadingText="Loading check airmen..."
                    :items="checkAirmans"
                    v-model="selectedCheckAirman"
                    v-model:searchText="airmanSearch"
                    :validationError="airmanValidationError"
                    :touched="airmanTouched"
                    noResultsText="No matching check airmen found"
                    :searchFields="['firstName', 'lastName']"
                    displayField="fullName"
                    @select="selectAirman"
                    @blur="handleAirmanBlur"
                >
                    <template #item="{ item }">
                        {{ item.firstName }} {{ item.lastName }}
                    </template>
                </SearchDropdown>
            </div>
        </div>
        
        <!-- Submit Button - Only enabled when all validations pass -->
        <div class="field is-grouped mt-4">
            <div class="control is-expanded">
                <button class="button is-info is-fullwidth" @click="submitCheckride" 
                        :disabled="!isFormValid || isSubmitting"
                        :class="{ 'is-loading': isSubmitting }">
                    {{ isSubmitting ? 'Creating Checkride...' : 'Submit' }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue';
import SearchDropdown from '@/components/SearchDropdown.vue';
import { useCheckridesStore } from '@/store/checkridesStore';
import { useCheckrides } from '@/lib/checkrides';
import { functions, appwriteConfig } from '@/lib/appwrite';

export default {
    name: 'New299Form',
    components: {
        SearchDropdown
    },
    emits: ['success', 'error'],
    setup(props, { emit }) {
        const selectedAircraft = ref('');
        const aircrafts = ref([]);
        const selectedLegs = ref([]);
        const availableLegs = ref([]);
        const legsLoading = ref(false);
        const tailNumbersLoading = ref(false);
        const selectedCheckAirman = ref('');
        const checkAirmans = ref([]);
        const checkAirmansLoading = ref(false);
        const isSubmitting = ref(false);
        
        // Validation errors
        const aircraftValidationError = ref('');
        const legsValidationError = ref('');
        const airmanValidationError = ref('');
        
        // Search states
        const tailNumberSearch = ref('');
        const airmanSearch = ref('');
        
        // Get stores
        const checkridesStore = useCheckridesStore();
        const checkrides = useCheckrides();

        // Touched flags
        const aircraftTouched = ref(false);
        const legsTouched = ref(false);
        const airmanTouched = ref(false);

        // Validation functions
        const validateAircraft = () => {
            if (!selectedAircraft.value) {
                aircraftValidationError.value = "Please select an aircraft tail number";
                return false;
            }
            aircraftValidationError.value = '';
            return true;
        };

        const validateLegs = () => {
            if (selectedLegs.value.length === 0) {
                legsValidationError.value = "Please select at least one leg";
                return false;
            }
            
            const firstPicId = getPilotId(selectedLegs.value[0], 2);
            if (!firstPicId) {
                legsValidationError.value = "Selected leg does not have a PIC assigned";
                return false;
            }
            
            const picMatchesResult = selectedLegs.value.every(leg => getPilotId(leg, 2) === firstPicId);
            if (!picMatchesResult) {
                legsValidationError.value = "PIC must match on all selected legs";
                return false;
            }

            // Require a SIC on the first selected leg to support server-side validation
            const firstSicId = getPilotId(selectedLegs.value[0], 3);
            if (!firstSicId) {
                legsValidationError.value = "Selected leg does not have a SIC assigned";
                return false;
            }
            
            legsValidationError.value = '';
            return true;
        };

        const validateCheckAirman = () => {
            if (!selectedCheckAirman.value) {
                airmanValidationError.value = "Please select a check airman";
                return false;
            }
            airmanValidationError.value = '';
            return true;
        };
        
        // Computed validation states
        const isAircraftValid = computed(() => {
            if (!aircraftTouched.value && !selectedAircraft.value) return false;
            validateAircraft();
            return selectedAircraft.value && !aircraftValidationError.value;
        });
        
        const isLegsValid = computed(() => {
            if (!isAircraftValid.value) return false;
            if (!legsTouched.value && selectedLegs.value.length === 0) return false;
            validateLegs();
            return selectedLegs.value.length > 0 && !legsValidationError.value;
        });
        
        const isCheckAirmanValid = computed(() => {
            if (!isLegsValid.value) return false;
            if (!airmanTouched.value && !selectedCheckAirman.value) return false;
            validateCheckAirman();
            return selectedCheckAirman.value && !airmanValidationError.value;
        });
        
        // Overall form validity
        const isFormValid = computed(() => {
            return isAircraftValid.value && isLegsValid.value && isCheckAirmanValid.value;
        });

        // Initialize data
        onMounted(async () => {
            try {
                tailNumbersLoading.value = true;
                
                // Initialize checkrides library
                checkrides.initialize();
                
                // Fetch tail numbers
                const tailNumbers = await checkridesStore.fetchTailNumbers();
                aircrafts.value = Object.entries(tailNumbers).map(([id, value]) => ({
                    id,
                    value
                }));
            } catch (error) {
                console.error('Error fetching tail numbers:', error);
                emit('error', "Failed to load aircraft data. Please try again.");
            } finally {
                tailNumbersLoading.value = false;
            }
        });

        // Utility functions
        const formatDate = (timestamp) => {
            if (!timestamp) return '';
            const options = { year: 'numeric', month: 'short', day: 'numeric' };
            return new Date(timestamp).toLocaleDateString(undefined, options);
        };

        const formatTime = (timestamp) => {
            if (!timestamp) return '';
            const options = { hour: '2-digit', minute: '2-digit' };
            return new Date(timestamp).toLocaleTimeString(undefined, options);
        };

        const getPilotName = (leg, position) => {
            if (!leg.pilots || !leg.pilots[position]) return '';
            const pilot = leg.pilots[position];
            return `${pilot.firstName} ${pilot.lastName}`;
        };

        const getPilotId = (leg, position) => {
            if (!leg.pilots || !leg.pilots[position]) return '';
            const pilot = leg.pilots[position];
            return pilot.pilotId;
        };

        const toggleLegSelection = (leg) => {
            const index = selectedLegs.value.findIndex(selectedLeg => selectedLeg.id === leg.id);
            if (index === -1) {
                selectedLegs.value.push(leg);
            } else {
                selectedLegs.value.splice(index, 1);
            }
            legsTouched.value = true;
        };
        
        const selectAircraft = () => {
            aircraftTouched.value = true;
            loadLegs();
            validateAircraft();
        };
        
        const selectAirman = () => {
            airmanTouched.value = true;
            validateCheckAirman();
        };

        const handleAircraftBlur = () => {
            if (tailNumberSearch.value) {
                aircraftTouched.value = true;
                validateAircraft();
            }
        };

        const handleAirmanBlur = () => {
            if (airmanSearch.value) {
                airmanTouched.value = true;
                validateCheckAirman();
            }
        };

        const loadLegs = async () => {
            if (!selectedAircraft.value) {
                availableLegs.value = [];
                selectedLegs.value = [];
                return;
            }
            
            legsLoading.value = true;
            try {
                const legs = await checkrides.fetchLegs(selectedAircraft.value);
                availableLegs.value = legs;
                selectedLegs.value = [];
            } catch (error) {
                emit('error', "Failed to load legs. Please try again.");
                availableLegs.value = [];
                selectedLegs.value = [];
            } finally {
                legsLoading.value = false;
            }
        };

        const loadCheckAirmans = async () => {  
            checkAirmansLoading.value = true;
            try {
                const airmenData = await checkrides.fetchCheckAirmen();
                
                checkAirmans.value = Object.entries(airmenData).map(([id, airman]) => ({
                    id,
                    firstName: airman.firstName,
                    lastName: airman.lastName,
                    fullName: `${airman.firstName} ${airman.lastName}`
                }));
            } catch (error) {
                console.error('Error fetching check airmen:', error);
                emit('error', "Failed to load check airmen. Please try again.");
                checkAirmans.value = [];
            } finally {
                checkAirmansLoading.value = false;
            }
        };

        // Watch for changes to legs to load check airmen
        watch(selectedLegs, () => {
            selectedCheckAirman.value = '';
            airmanSearch.value = '';
            airmanValidationError.value = '';
            airmanTouched.value = false;
            
            if (legsTouched.value) {
                validateLegs();
            }
            
            if (isLegsValid.value) {
                loadCheckAirmans();
            }
        }, { deep: true });

        const submitCheckride = async () => {
            // Mark all sections as touched when attempting to submit
            aircraftTouched.value = true;
            legsTouched.value = true;
            airmanTouched.value = true;
            
            // Validate all sections before submitting
            if (!validateAircraft() || !validateLegs() || !validateCheckAirman()) {
                return;
            }

            // Group legs by dispatchId
            const legsByDispatch = {};
            selectedLegs.value.forEach(leg => {
                if (!legsByDispatch[leg.dispatchId]) {
                    legsByDispatch[leg.dispatchId] = [];
                }
                legsByDispatch[leg.dispatchId].push(leg.id);
            });
            
            // Scheduled date/time is the latest (last) selected leg's departure time
            const lastLegTime = new Date(Math.max(...selectedLegs.value.map(l => l.departureTime || 0))).toISOString();

            // PIC/SIC IDs from the first selected leg
            const picId = getPilotId(selectedLegs.value[0], 2);
            const sicId = getPilotId(selectedLegs.value[0], 3);

            const data = {
                type: '299',
                aircraftId: selectedAircraft.value,
                legs: legsByDispatch,
                checkAirmanId: selectedCheckAirman.value,
                picId,
                sicId,
                scheduledDateTime: lastLegTime
            };

            try {
                isSubmitting.value = true;
                const response = await functions.createExecution(
                    appwriteConfig.functions.createCheckride,
                    JSON.stringify(data)
                );
                const result = JSON.parse(response.responseBody);
                
                if (result.success) {
                    // Reset form
                    selectedAircraft.value = '';
                    tailNumberSearch.value = '';
                    selectedLegs.value = [];
                    availableLegs.value = [];
                    selectedCheckAirman.value = '';
                    airmanSearch.value = '';
                    aircraftValidationError.value = '';
                    legsValidationError.value = '';
                    airmanValidationError.value = '';
                    
                    // Reset touched states
                    aircraftTouched.value = false;
                    legsTouched.value = false;
                    airmanTouched.value = false;
                    
                    emit('success', result.checkride);
                } else {
                    emit('error', `Failed to create checkride: ${result.error}`);
                }
            } catch (error) {
                console.error('Error submitting checkride:', error);
                emit('error', `Error submitting checkride: ${error.message}`);
            } finally {
                isSubmitting.value = false;
            }
        };

        return {
            selectedAircraft,
            aircrafts,
            selectedLegs,
            availableLegs,
            legsLoading,
            tailNumbersLoading,
            selectedCheckAirman,
            checkAirmans,
            checkAirmansLoading,
            isSubmitting,
            aircraftValidationError,
            legsValidationError,
            airmanValidationError,
            tailNumberSearch,
            airmanSearch,
            isAircraftValid,
            isLegsValid,
            isCheckAirmanValid,
            isFormValid,
            aircraftTouched,
            legsTouched,
            airmanTouched,
            formatDate,
            formatTime,
            getPilotName,
            toggleLegSelection,
            selectAircraft,
            selectAirman,
            handleAircraftBlur,
            handleAirmanBlur,
            submitCheckride
        };
    }
};
</script>

<style scoped>
.cursor-pointer {
    cursor: pointer;
}
</style>